import SettingsIcon from '@mui/icons-material/Settings';
import {Box, Divider, IconButton, Typography} from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Switch from '@mui/material/Switch';
import {YSThemeStyleRadius} from 'main/utility/AppContextProvider/YSTheme';
import PropTypes from 'prop-types';
import React from 'react';
import ClickAwayListener from 'react-click-away-listener';
import {TableColumnVisibility} from 'shared/constants/UiWisePropType';

const AppsColumnSetting = ({
  dataColumns = [],
  onColumnVisibilityChange,
  onSelectAllColumnsVisibility,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <Box sx={{position: 'relative'}}>
      <IconButton
        onClick={handleClick}
        sx={{
          color: open ? 'primary.main' : 'secondary.main', // Turns Gold when active
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'rotate(45deg)',
          },
        }}
      >
        <SettingsIcon sx={{fontSize: '1em'}} />
      </IconButton>

      {open && (
        <ClickAwayListener onClickAway={handleClose}>
          <Box
            sx={{
              top: '110%', // Slight gap for "Executive" breathing room
              right: 0,
              bgcolor: 'background.paper',
              borderRadius: `${YSThemeStyleRadius.SHARP}px`,
              boxShadow: (theme) => theme.palette.contextMenuShadow,
              p: 4,
              zIndex: 10,
              position: 'absolute',
              minWidth: 220,
              border: '1px solid rgba(26, 26, 27, 0.05)',
            }}
          >
            <Typography variant='h4' sx={{mb: 3, fontSize: 12, opacity: 0.7}}>
              Column Visibility
            </Typography>

            <FormGroup>
              {dataColumns.map(
                (col) =>
                  col.showable && (
                    <FormControlLabel
                      key={col.name}
                      label={col.label}
                      sx={{
                        mb: 0.25,
                        '& .MuiTypography-root': {
                          fontSize: 12,
                          letterSpacing: '0.2px',
                        },
                      }}
                      control={
                        <Switch
                          checked={col.show}
                          size='small'
                          name={col.name}
                          onChange={onColumnVisibilityChange}
                          sx={{
                            transform: 'scale(0.8)', // Surgical shrinking for this specific popup
                            mr: 1.5,
                          }}
                        />
                      }
                    />
                  ),
              )}
            </FormGroup>

            <Divider sx={{my: 3}} />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 3,
                px: 1,
              }}
            >
              <Typography
                component='span'
                onClick={() => onSelectAllColumnsVisibility(false)}
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'secondary.main', // Onyx
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  transition: 'all 0.2s',
                  '&:hover': {
                    opacity: 0.7,
                    textDecoration: 'underline',
                    textUnderlineOffset: '4px',
                  },
                }}
              >
                Hide All
              </Typography>

              <Typography
                component='span'
                onClick={() => onSelectAllColumnsVisibility(true)}
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'primary.dark', // Readability Gold
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  transition: 'all 0.2s',
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'underline',
                    textUnderlineOffset: '4px',
                  },
                }}
              >
                Show All
              </Typography>
            </Box>
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
};

AppsColumnSetting.propTypes = {
  dataColumns: PropTypes.arrayOf(TableColumnVisibility).isRequired,
  onColumnVisibilityChange: PropTypes.func.isRequired,
  onSelectAllColumnsVisibility: PropTypes.func.isRequired,
};

export default AppsColumnSetting;
